import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Plus, Trash2, Shield, Eye, UserCheck, Edit } from 'lucide-react';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';

interface User {
  id: number;
  username: string;
  role: 'admin' | 'read-only';
  createdAt: string;
}

interface AddEditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingUser?: User | null;
}

function AddEditUserModal({ isOpen, onClose, editingUser }: AddEditUserModalProps) {
  const [formData, setFormData] = useState({
    username: editingUser?.username || '',
    password: '',
    role: editingUser?.role || 'read-only' as 'admin' | 'read-only'
  });
  const { toast } = useToast();
  
  // Reset form when modal opens/closes or user changes
  React.useEffect(() => {
    setFormData({
      username: editingUser?.username || '',
      password: '',
      role: editingUser?.role || 'read-only'
    });
  }, [isOpen, editingUser]);

  const createUserMutation = useMutation({
    mutationFn: (userData: typeof formData) => 
      apiRequest('POST', '/api/admin/users', userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'User created successfully' });
      onClose();
      setFormData({ username: '', password: '', role: 'read-only' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to create user', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: (userData: typeof formData) => 
      apiRequest('PUT', `/api/admin/users/${editingUser?.id}`, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'User updated successfully' });
      onClose();
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to update user', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username) {
      toast({ title: 'Username is required', variant: 'destructive' });
      return;
    }
    if (!editingUser && !formData.password) {
      toast({ title: 'Password is required for new users', variant: 'destructive' });
      return;
    }
    
    if (editingUser) {
      updateUserMutation.mutate(formData);
    } else {
      createUserMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              autoComplete="username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="Enter username"
              required
              disabled={!!editingUser} // Don't allow changing username
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">
              {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder={editingUser ? "Enter new password or leave blank" : "Enter password"}
              required={!editingUser}
            />
            <PasswordStrengthIndicator password={formData.password} />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select
              value={formData.role}
              onValueChange={(value: 'admin' | 'read-only') => setFormData({ ...formData, role: value })}
            >
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="read-only">Read Only</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createUserMutation.isPending || updateUserMutation.isPending}>
              {createUserMutation.isPending || updateUserMutation.isPending ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function UserManagement() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { user: currentUser, isAdmin } = useAuth();
  const { toast } = useToast();

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => apiRequest<User[]>('/api/admin/users'),
    enabled: isAdmin()
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) => 
      apiRequest('DELETE', `/api/admin/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast({ title: 'User deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Failed to delete user', variant: 'destructive' });
    }
  });

  const handleDeleteUser = (user: User) => {
    if (user.id === currentUser?.id) {
      toast({ title: 'Cannot delete your own account', variant: 'destructive' });
      return;
    }
    
    if (confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      deleteUserMutation.mutate(user.id);
    }
  };

  if (!isAdmin()) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Shield className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-gray-600">You need admin privileges to manage users.</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Users className="w-16 h-16 mx-auto text-blue-400 mb-4 animate-pulse" />
          <p className="text-gray-600">Loading users...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Management
            </CardTitle>
            <Button onClick={() => setIsAddModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {users?.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {user.role === 'admin' ? (
                      <Shield className="w-5 h-5 text-red-500" />
                    ) : (
                      <Eye className="w-5 h-5 text-blue-500" />
                    )}
                    <div>
                      <p className="font-medium">{user.username}</p>
                      {user.id === currentUser?.id && (
                        <p className="text-sm text-gray-500">(You)</p>
                      )}
                    </div>
                  </div>
                  <Badge variant={user.role === 'admin' ? 'destructive' : 'secondary'}>
                    {user.role === 'admin' ? 'Admin' : 'Read Only'}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    Created: {new Date(user.createdAt).toLocaleDateString()}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingUser(user)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  {user.id !== currentUser?.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteUser(user)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {!users?.length && (
              <div className="text-center py-8">
                <UserCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No users found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AddEditUserModal
        isOpen={isAddModalOpen || !!editingUser}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingUser(null);
        }}
        editingUser={editingUser}
      />
    </div>
  );
}